/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { getGuid } from 'src/core/utils/randomValueGenerators';
import { getBlobUriByRegion } from '../utils/qnaMakerBlobUri';

/**
 * Represents a service for common QnA services.
 */
export const getQnaMakerCommonService = createService(({ getEnvironment, context }) => {
    const { authWebApiUrl, isProduction } = getEnvironment({ apiName: 'qna' });
    const { currentResourceRegion } = context;

    /**
     * Get the SAS url of the qna storage to be used in uploading files.
     *
     * @returns The SAS Uri.
     */
    const getSASUri = async (): Promise<{ endpoint: string; token: string }> => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('qnaMaker', 'sasUri')
            .queryParam('region', currentResourceRegion)
            .queryParam('isProduction', `${isProduction}`)
            .build();
        const apiOptions = luisRequestOptionsBuilder()
            .useContentType('json')
            .useCORS()
            .build();

        const { data } = await httpService.get<{ endpoint: string; token: string }>(url, apiOptions);

        return data;
    };

    /**
     * Get the blob url of the qna storage to be used in uploading files.
     *
     * @returns The blob Uri.
     */
    const getBlobUri = (): string => getBlobUriByRegion(currentResourceRegion, isProduction);

    /**
     * Upload files to the blob (by region) and returns the file path.
     *
     * @returns The endpoint for the file in the storage container.
     */
    const uploadFiles = async ({ file }: { file: File }): Promise<string> => {
        const blobName = `${getGuid()}-${file.name}`;
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('qnaMaker', 'uploadFile')
            .queryParam('region', currentResourceRegion)
            .queryParam('isProduction', `${isProduction}`)
            .queryParam('blobName', blobName)
            .build();
        const apiOptions = luisRequestOptionsBuilder()
            .useContentType('bin')
            .useCORS()
            .build();
        apiOptions.headers['x-ms-blob-type'] = 'BlockBlob';
        apiOptions.headers['x-ms-version'] = '2020-08-04';

        await httpService.post<void>(url, file as Object, apiOptions);
        return `${getBlobUriByRegion(currentResourceRegion, isProduction)}/${blobName}`;
    };
    /**
     * Upload files to the sas uri using key and return the file path. Used in case MI upload fails.
     *
     * @returns The endpoint for the file in the storage container.
     */
    const uploadFilesWithKey = async ({
        file,
        sasEndpoint,
        sasToken
    }: {
        file: File;
        sasEndpoint: string;
        sasToken: string;
    }): Promise<string> => {
        const filePath = `${sasEndpoint}/${getGuid()}-${file.name}`;
        const blobName = `${getGuid()}-${file.name}`;
        const url = `${filePath}?${sasToken}`;
        const apiOptions = luisRequestOptionsBuilder()
            .useContentType('bin')
            .useCORS()
            .build();
        apiOptions.headers['x-ms-blob-type'] = 'BlockBlob';
        apiOptions.headers['x-ms-version'] = '2020-08-04';

        await httpService.post<void>(url, file as Object, apiOptions);
        return `${getBlobUriByRegion(currentResourceRegion, isProduction)}/${blobName}`;
    };
    return {
        getSASUri,
        uploadFiles,
        uploadFilesWithKey,
        getBlobUri
    };
});
